import React, { useEffect, useState } from 'react';
import { Button, message } from 'antd';
import { red } from '@ant-design/colors';
import { PlusCircleOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { Page } from '../../../../components.js';
import { FormData, NewBigBagForm } from './components/new-big-bag-form';
import { useNewBigBagOrder } from './use-new-big-bag-order';
import styled from 'styled-components';
import { OrderType } from 'types';
import qs from 'qs';

import '../delivery/styles/newDelivery.scss';

const LockedMessage = styled.span`
	color: ${red[5]} !important;
	font-weight: 400 !important;
	font-size: 16px !important;
`;

interface Props {
	select: React.ReactNode;
}

export const NewBigBagOrder: React.FC<Props> = ({ select }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const query = qs.parse(location.search, {
		ignoreQueryPrefix: true,
	});

	const [value, setValue] = useState<FormData>({ count: '1' });
	const [adding, setAdding] = useState(false);
	const { createOrder } = useNewBigBagOrder();

	useEffect(() => {
		const clientRaw = query['client'];
		if (!clientRaw) {
			return;
		}

		const client = JSON.parse(atob(clientRaw as string));
		if (!client || !client.id) {
			return;
		}

		setValue((prev) => ({
			...prev,
			client,
		}));
		navigate('.?type=3', {
			replace: true,
		});
	}, [query['client']]);

	const onCreateOrder = async () => {
		if (!value.client) {
			message.error('Nie wybrano klienta');
			return;
		}

		if (value.bdo === undefined) {
			message.error('Nie wybrano BDO');
			return;
		}

		const wasteType = value.wasteType?.trim() ?? null;
		if (!wasteType || wasteType === '') {
			message.error('Nie wybrano typu odpadu');
			return;
		}

		const address = value.address?.trim() ?? null;
		if (!address || address === '') {
			message.error('Nie podano adresu');
			return;
		}

		const contact = value.contact?.trim() ?? undefined;

		const countRaw = value.count;
		if (!countRaw || countRaw === '') {
			message.error('Nie podano ilości worków');
			return;
		}

		const count = Number.parseInt(countRaw, 10);
		if (isNaN(count)) {
			message.error('Ilość worków musi być liczbą');
			return;
		}

		if (count < 1 || count > 255) {
			message.error('Ilość worków musi być z zakresu 1 - 255');
			return;
		}

		const priceRaw = value.price?.trim() ?? null;
		if (!priceRaw || priceRaw === '') {
			message.error('Nie podano ceny');
			return;
		}

		const price = Number.parseFloat(priceRaw);
		if (Number.isNaN(price)) {
			message.error('Cena musi być liczbą');
			return;
		}

		if (price < 0) {
			message.error('Cena musi być dodatnia');
			return;
		}

		if (price > 999999.99) {
			message.error('Cena nie może być większa od 999 999.99 PLN');
			return;
		}

		const wantedAdvanceRaw = value.wantedAdvance?.trim() ?? null;
		const wantedAdvance = wantedAdvanceRaw ? Number.parseFloat(wantedAdvanceRaw) : null;
		if (wantedAdvance) {
			if (Number.isNaN(wantedAdvance)) {
				message.error('Zaliczka musi być liczbą');
				return;
			}

			if (wantedAdvance < 0) {
				message.error('Zaliczka musi być dodatnia');
				return;
			}

			if (wantedAdvance > 999999.99) {
				message.error('Zaliczka nie może być większa od 999 999.99 PLN');
				return;
			}

			if (wantedAdvance > price) {
				message.error('Zaliczka nie może być większa od ceny');
				return;
			}
		}

		if (value.priceType === undefined) {
			message.error('Nie wybrano typu ceny');
			return;
		}

		if (value.wantedPaymentType === undefined) {
			message.error('Nie wybrano typu płatności');
			return;
		}

		const comments = value.comments?.trim() ?? undefined;

		if (!value.deliveryDate) {
			message.error('Nie wybrano terminu podstawienia');
			return;
		}

		setAdding(true);

		const order = await createOrder(
			{
				clientId: value.client.id,
				bdo: value.bdo,
				wasteType,
				address,
				contact: contact === '' ? undefined : contact,
				onPlace: value.onPlace ?? false,
				count,
				price,
				wantedAdvance,
				priceType: value.priceType,
				wantedPaymentType: value.wantedPaymentType,
				comments: comments === '' ? undefined : comments,
				deliveryDate: value.deliveryDate,
				claimingDate: value.claimingDate,
				assignee: undefined, // TODO: Add possibility to choose assignee
			},
			value.isNewAddress ?? true
		);

		setAdding(false);

		if (order) {
			navigate(`/panel/orders/${order.id}/${OrderType.loose}`, { replace: true });
		}
	};

	const actions = [
		<Button
			key="0"
			type="primary"
			size="large"
			icon={<PlusCircleOutlined />}
			onClick={onCreateOrder}
			loading={adding}
			disabled={!!value.client?.lockedDate}
		>
			Dodaj zamówienie
		</Button>,
	];
	if (value.client?.lockedDate) {
		actions.push(<LockedMessage>Klient zablokowany!</LockedMessage>);
	}

	return (
		<Page title="Zamówienia / Nowe" actions={actions}>
			<NewBigBagForm
				select={select}
				value={value}
				onChange={(n) =>
					setValue((o) => ({
						...o,
						...n,
					}))
				}
			/>
		</Page>
	);
};
